import { Button, EmptyState } from '@bloom/ui';
import { Compass } from 'lucide-react';
import type { ComponentType } from 'react';
import { Link, Navigate, type RouteObject } from 'react-router';
import { Page } from '../components/page';
import { COMMON } from '../copy/common';
import { GuestOnly, RequireAdmin, RequireAuth } from './guards';
import { AppLayout } from './layout';

type Loader<K extends string> = () => Promise<Record<K, ComponentType>>;

/** Pages load on demand; `name` is the export (`default` for most). */
const page =
  <K extends string = 'default'>(load: Loader<K>, name?: K) =>
  async () => {
    const module = await load();
    return { Component: module[(name ?? 'default') as K] };
  };

function NotFound() {
  return (
    <Page>
      <EmptyState
        titleAs="h1"
        icon={<Compass className="size-5" />}
        title={COMMON.notFoundTitle}
        description={COMMON.notFoundBody}
        action={
          <Button asChild>
            <Link to="/">{COMMON.toBoard}</Link>
          </Button>
        }
      />
    </Page>
  );
}

/**
 * Route map. Guards are UX: they route people to what they can use; every API call behind
 * these pages is authorized server-side regardless.
 *
 *   /                  board                     /me, /me/profile, /agreements/:id   signed in
 *   /t/:code           trial page                /trials/new, /trials/:id/edit       signed in
 *   /login             email-code sign-in        /admin/{queue,queue/:id,rules,agreements}  moderators
 */
export const routes: RouteObject[] = [
  {
    element: <AppLayout />,
    children: [
      { index: true, lazy: page(() => import('../features/market/board-page')) },
      { path: 't/:code', lazy: page(() => import('../features/market/trial-page')) },
      {
        element: <GuestOnly />,
        children: [{ path: 'login', lazy: page(() => import('../features/auth/login-page')) }],
      },
      {
        element: <RequireAuth />,
        children: [
          {
            path: 'trials/new',
            lazy: page(() => import('../features/market/new-trial-page'), 'NewTrialPage'),
          },
          { path: 'me', lazy: page(() => import('../features/me/me-page')) },
          { path: 'me/profile', lazy: page(() => import('../features/market/profile-page')) },
          { path: 'agreements/:id', lazy: page(() => import('../features/me/agreement-page')) },
          {
            path: 'trials/:id/edit',
            lazy: page(() => import('../features/market/new-trial-page'), 'EditTrialPage'),
          },
        ],
      },
      {
        element: <RequireAdmin />,
        children: [
          {
            path: 'admin',
            lazy: page(() => import('../features/admin/admin-shell')),
            children: [
              { index: true, element: <Navigate to="queue" replace /> },
              {
                path: 'queue',
                lazy: page(() => import('../features/admin/queue-page'), 'QueuePage'),
              },
              {
                path: 'queue/:id',
                lazy: page(() => import('../features/admin/case-page'), 'CasePage'),
              },
              {
                path: 'rules',
                lazy: page(() => import('../features/admin/rules-page'), 'RulesPage'),
              },
              {
                path: 'agreements',
                lazy: page(() => import('../features/admin/agreements-page'), 'AgreementsPage'),
              },
            ],
          },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
];
